import { memo, type ReactNode } from 'react';
import type { DiagnosticGroup } from '../diagnostics/group';
import type { CompileResult, Severity } from '../engine/types';
import { formatNumber, formatPercent, useI18n } from '../i18n';
import type { AnalysisState } from '../lib/analysis';
import { kindKey, shapeLine, shapeLineText } from '../lib/shape/line';
import { composeSummary } from '../lib/summary';
import { Icon, SeverityIcon } from '../ui/Icon';
import { StitchMeter } from '../ui/Knit';

const SEV_TEXT: Record<Severity, string> = { error: 'text-error', warning: 'text-warning', info: 'text-info' };

function Field({ label, children, testId }: { label: string; children: ReactNode; testId?: string }) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <dt className="kicker">{label}</dt>
      <dd className="mt-1 text-sm text-fg">{children}</dd>
    </div>
  );
}

interface Props {
  result: CompileResult | null;
  analysis: AnalysisState | null;
  /** Grouped (line, code) counts, consistent with the findings. */
  counts: Record<Severity, number>;
  groups: DiagnosticGroup[];
  /** Shown when the pattern has no `# title:` (e.g. the loaded example's name). */
  fallbackTitle?: string | null;
  variant?: 'notebook' | 'compact';
  selectedSize?: number | null;
  onSize?: (s: number | null) => void;
}

/**
 * The pattern's head: title, what kind of pattern, the summary sentence (notebook), per-size status,
 * gauge, coverage (a row of stitches) and the alert counts.
 */
export const DocumentHeader = memo(function DocumentHeader({
  result,
  counts: grouped,
  groups,
  fallbackTitle,
  analysis,
  variant = 'notebook',
  selectedSize = null,
  onSize,
}: Props) {
  const { t, lang } = useI18n();
  if (!result || !analysis) {
    return <div className="px-6 py-6 text-sm text-muted">{t('doc.waiting')}</div>;
  }
  const { document: doc, coverage, summary } = result;
  const compact = variant === 'compact';
  const pct = Math.round(coverage.ratio * 100);
  const meterTone = pct >= 95 ? 'accent' : pct >= 75 ? 'warning' : 'error';
  const traceSteps = result.module_trace?.length ?? 0;
  // Never "0 %" for a pattern a module verified; never a success look when nothing was understood.
  const byModule = analysis === 'module' && coverage.ratio === 0;
  const counts: Array<[Severity, number, number]> = [
    ['error', grouped.error, summary.n_errors],
    ['warning', grouped.warning, summary.n_warnings],
    ['info', grouped.info, summary.n_infos],
  ];
  const gauge = doc.gauge;
  const told = composeSummary(result, groups, analysis, lang);
  const title = doc.title ?? fallbackTitle ?? null;
  const meta = [t(`doc.langName.${doc.lang}`), doc.construction === 'round' ? t('doc.round') : t('doc.flat')];

  const countList = (
    <ul className="flex flex-wrap items-center gap-3" aria-label={t('doc.summaryAria')}>
      {analysis === 'none' && (
        <li className="inline-flex items-center gap-1.5 text-xs font-bold text-warning" title={t('doc.notVerifiedTitle')} data-testid="not-verified">
          <SeverityIcon severity="warning" size={13} />
          {t('doc.notVerified')}
        </li>
      )}
      {counts.map(([sev, n, perSize]) => (
        <li
          key={sev}
          className={`inline-flex items-center gap-1.5 text-sm font-bold tabular ${n > 0 ? SEV_TEXT[sev] : 'text-subtle'}`}
          title={t('doc.countTitle', { label: t(`sevs.${sev}`), n, perSize })}
          data-testid={`count-${sev}`}
        >
          <SeverityIcon severity={sev} size={13} className={n === 0 ? 'opacity-40' : ''} />
          {n}
          <span className="sr-only">{t(`sevs.${sev}`)}</span>
        </li>
      ))}
    </ul>
  );

  const coverageField = (
    <div className="min-w-0" data-testid="coverage">
      <div className="kicker">{t('doc.coverage')}</div>
      {byModule ? (
        <>
          <div className="mt-1 inline-flex items-center gap-1 text-sm font-bold text-accent-strong" data-testid="coverage-module">
            <Icon name="check" size={12} />
            {t('doc.moduleVerified')}
          </div>
          <div className="text-2xs text-subtle">{t('doc.moduleDetail', { n: traceSteps })}</div>
        </>
      ) : (
        <>
          <div className="mt-1 flex items-center gap-3">
            <span className={`shrink-0 text-sm font-bold tabular ${analysis === 'none' ? 'text-warning' : 'text-fg'}`}>
              {analysis === 'none' ? t('doc.noneUnderstood') : formatPercent(lang, pct)}
            </span>
            {analysis !== 'none' && (
              <StitchMeter
                percent={pct}
                tone={meterTone}
                label={t('doc.coverage')}
                title={t('doc.coverageDetail', { n: coverage.n_understood, total: coverage.n_rows })}
              />
            )}
          </div>
          <div className="mt-0.5 text-2xs text-subtle">
            {analysis === 'module' ? t('doc.moduleDetail', { n: traceSteps }) : t('doc.coverageDetail', { n: coverage.n_understood, total: coverage.n_rows })}
          </div>
        </>
      )}
    </div>
  );

  const gaugeText = gauge ? (
    <span title={gauge.raw}>
      <span data-verbatim lang={doc.lang}>
        {gauge.raw}
      </span>
      {gauge.sts_per_cm != null && gauge.rows_per_cm != null && (
        <span className="block text-2xs text-subtle">
          {t('doc.perCm', { sts: formatNumber(lang, gauge.sts_per_cm, 2), rows: formatNumber(lang, gauge.rows_per_cm, 2) })}
        </span>
      )}
    </span>
  ) : (
    <span className="text-subtle">{t('doc.noGauge')}</span>
  );

  const heading = (
    <h2
      className={`font-display min-w-0 text-fg ${compact ? 'truncate text-xl font-[480] leading-tight' : 'text-3xl font-[420] leading-[1.15]'}`}
      title={title ?? undefined}
      data-testid="document-title"
      data-verbatim={title ? true : undefined}
    >
      {title ? (
        <span data-verbatim lang={doc.lang}>
          {title}
        </span>
      ) : (
        <span className="italic text-muted">{t('doc.untitled')}</span>
      )}
    </h2>
  );

  if (compact) {
    return (
      <section className="border-b border-line px-4 py-3" aria-label={t('nb.summary')} data-testid="document-header">
        <div className="flex items-start gap-4">
          <div className="min-w-0 flex-1">
            {heading}
            <p className="mt-1 text-2xs text-muted">
              {meta.join(' · ')} · <span className="tabular">{doc.sizes.length}</span> {t('doc.sizes').toLowerCase()}
            </p>
          </div>
          {countList}
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-[minmax(0,1fr)_minmax(10rem,auto)]">
          <Field label={t('doc.gauge')}>{gaugeText}</Field>
          {coverageField}
        </dl>
      </section>
    );
  }

  return (
    <section aria-labelledby="nb-summary-title" data-testid="document-header">
      <h2 id="nb-summary-title" className="sr-only">
        {t('nb.summary')}
      </h2>
      <p className="kicker">{meta.join(' · ')}</p>
      <div className="mt-2">{heading}</div>
      {doc.source && (
        <p className="mt-1 text-xs text-subtle" data-verbatim>
          {doc.source}
        </p>
      )}
      <p
        className={`font-display mt-5 text-xl font-[380] leading-[1.45] ${told.tone === 'none' ? 'text-warning' : 'text-fg'}`}
        data-testid="summary-sentence"
      >
        {told.sentences.map((sentence, i) => (
          <span key={i}>
            {i > 0 && ' '}
            {sentence.map((part, j) =>
              typeof part === 'string' ? (
                <span key={j}>{part}</span>
              ) : (
                <span key={j} className="font-[520]" data-verbatim lang={doc.lang}>
                  {part.verbatim}
                </span>
              ),
            )}
          </span>
        ))}
      </p>
      {told.sizes.length > 1 && (
        <ul className="mt-4 flex flex-wrap gap-2" aria-label={t('sum.sizesAria')} data-testid="size-status">
          {told.sizes.map((s) => {
            const state =
              s.errors === 0 ? t(s.checked ? 'sum.sizeOk' : 'sum.sizeUnknown') : s.errors === 1 ? t('sum.sizeErrors1') : t('sum.sizeErrorsN', { n: s.errors });
            const active = selectedSize === s.size;
            return (
              <li key={s.size}>
                <button
                  type="button"
                  onClick={() => onSize?.(active ? null : s.size)}
                  aria-pressed={active}
                  title={t('sum.sizeTitle', { size: s.name, state })}
                  data-state={s.errors ? 'error' : s.checked ? 'ok' : 'unknown'}
                  className={`inline-flex items-center gap-2 rounded-pill py-1 pl-3 pr-3 text-xs font-semibold transition-colors ${
                    s.errors ? 'bg-error/[0.09] text-error' : s.checked ? 'bg-accent/[0.09] text-accent-strong' : 'bg-sunken text-muted'
                  } ${active ? 'ring-1 ring-inset ring-current' : ''}`}
                >
                  <span className="font-bold text-fg">{s.name}</span>
                  {s.errors ? (
                    <span className="inline-flex items-center gap-1">
                      <SeverityIcon severity="error" size={11} />
                      {state}
                    </span>
                  ) : !s.checked ? (
                    <span>{state}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <Icon name="check" size={11} />
                      {state}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <ShapeLineText result={result} selectedSize={selectedSize} />
      <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-3">
        <Field label={t('doc.gauge')}>{gaugeText}</Field>
        {coverageField}
        <Field label={t('doc.summaryAria')}>{countList}</Field>
      </dl>
    </section>
  );
});

/**
 * « Forme : bonnet de 56 cm de tour, 22 cm de haut » (the chosen size, or the range over the sizes);
 * a yoke names itself (« Empiècement : encolure 44 cm, … »).
 */
function ShapeLineText({ result, selectedSize }: { result: CompileResult; selectedSize: number | null }) {
  const { t, lang } = useI18n();
  const line = shapeLine(result, selectedSize);
  if (!line) return null;
  const { text, labelled } = shapeLineText(line, t(kindKey(line.kind, result)), t, lang);
  return (
    <p className="mt-4 text-sm text-muted" data-testid="shape-line">
      {labelled ? text : t('sum.shape', { text })}
    </p>
  );
}
